<template>
    <div
        class="form-group -d-block has-effect input-currency input-currency-sm"
        :class="[
            { '--tooltip': tooltip },
            `--text-${textAlign}`
        ]"
    >
        <div
            class="input-currency__wrapper has-effect-input no-arrows"
            :class="wrapperCustomClass"
        >
            <input
                v-model="localPrice"
                class="form-control"
                type="number"
                pattern="[0-9]*"
                inputmode="decimal"
                step="0.01"
                :placeholder="placeholder"
                :disabled="disabled"
                :style="{ textAlign: `${textAlign} !important` }"
                :class="[
                    { 'has-content': localPrice },
                    { 'is-danger': error || invalid },
                    { 'form-control-sm': small }
                ]"
                @blur="onBlur"
                @focus="onFocus"
                @keypress="isNumber"
                @input="onInput"
            />

            <label class="form-label">
                <span v-if="label" v-text="label" />
                <span v-if="required" v-text="'*'" class="text-danger pl-1" />
            </label>

            <span class="input-currency__price">{{ localPrice | formatPrice }}</span>

            <a-icon
                v-if="tooltip"
                v-tooltip="tooltip"
                class="input-currency__tooltip"
                icon="Info"
            />
        </div>
        <div class="input-error pt-1" v-if="invalidFeedback || errorMsg">
            {{ invalidFeedback || errorMsg }}
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            error: false,
            errorMsg: '',
            localPrice: null,
            resetValue: false,
            allowSend: false,
            checkInitZero: true
        }
    },
    props: {
        placeholder: {
            type: Number,
            required: false
        },
        maxPromotionalPrice: {
            type: Number,
            required: false
        },
        positiveNumberValidation: {
            type: Boolean,
            default: true
        },
        maxValue: {
            type: Number,
            required: false
        },
        price: {
            // eslint-disable-next-line vue/require-prop-type-constructor
            type: Number | String,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        validate: {
            type: Boolean,
            default: true
        },
        small: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            required: false
        },
        initValidation: {
            type: Boolean,
            default: true
        },
        wrapperCustomClass: {
            type: String,
            default: ''
        },
        invalid: {
            type: Boolean,
            default: false
        },
        invalidFeedback: {
            type: String,
            default: undefined
        },
        tooltip: {
            type: String,
            default: ''
        },
        textAlign: {
            type: String,
            default: 'right'
        },
        required: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        checkPrice(validateDecimals = true) {
            const { maxValue, maxPromotionalPrice } = this

            this.localPrice =
                this.localPrice && this.localPrice.toString().replace(',', '.')
            if (validateDecimals) {
                this.localPrice = (+this.localPrice).toFixed(2)
            } else {
                this.localPrice = +this.localPrice
            }

            const price = +this.localPrice

            if (!this.validate) {
                this.errorMsg = ''
            } else if (maxPromotionalPrice && price > +maxPromotionalPrice) {
                // if price is higher than maxPromotionalPrice
                this.errorMsg = `Wartość nie może przekroczyć maksymalnej ceny produktu w promocji (${this.$options.filters.currency(
                    maxPromotionalPrice
                )})`
            } else if (maxValue && +price > +maxValue) {
                // if price is higher than maxValue
                this.errorMsg = `Wartość maksymalna: ${this.$options.filters.currency(
                    maxValue
                )}`
            } else if (
                // if price is not negative or zero
                this.positiveNumberValidation &&
                price === 0 &&
                this.initValidation &&
                !this.checkInitZero
            ) {
                this.errorMsg = 'Wartość musi być większa od zera'
            } else {
                this.errorMsg = ''
            }

            this.error = !!this.errorMsg

            setTimeout(() => {
                this.$emit('onValidate', this.localPrice)
            }, 0)
        },
        onBlur() {
            this.checkPrice()
            this.$emit('onBlur', this.localPrice)
        },
        onInput() {
            this.$emit('onInput', this.localPrice)
        },
        onFocus(event) {
            setTimeout(() => {
                event.target.select()
            }, 0)
        },
        isNumber(event) {
            // eslint-disable-next-line no-unneeded-ternary
            event = event ? event : window.event
            const charCode = event.which ? event.which : event.keyCode
            const containsDot =
                event.target.value.includes(',') ||
                event.target.value.includes('.')
            if (
                (charCode > 31 &&
                    (charCode < 48 || charCode > 57) &&
                    charCode !== 46 &&
                    charCode !== 44) ||
                (charCode === 46 && containsDot) ||
                (charCode === 44 && containsDot)
            ) {
                event.preventDefault()
            } else {
                return true
            }
        }
    },
    watch: {
        validate: {
            immediate: true,
            handler(validate) {
                if (validate) {
                    this.checkPrice(false)
                } else {
                    this.error = ''
                }
            }
        },
        price() {
            if (this.allowSend) {
                this.checkInitZero = false
            }
            this.localPrice = this.price
        },
        localPrice() {
            if (this.allowSend) {
                this.checkInitZero = false
            }
            if (this.localPrice && this.allowSend) {
                this.localPrice = this.localPrice.toString()
                this.$emit('onChange', this.localPrice)
            }
        }
    },
    created() {
        this.localPrice = (+this.price).toFixed(2)
        setTimeout(() => {
            this.allowSend = true
        }, 0)

        if (this.initValidation) this.checkPrice()
    }
}
</script>

<style lang="scss">
.input-currency {
    color: var(--color-primary);
    position: relative;

    input {
        padding-right: 2rem !important;
    }

    &__wrapper {
        &::after {
            content: 'zł';
            position: absolute;
            top: 50%;
            right: 0.75rem;
            width: 1rem;
            transform: translateY(-50%);
            font-size: 1em;
            color: currentColor;
        }
    }

    &__tooltip {
        color: var(--color-primary);
        width: 20px;
        position: absolute;
        top: 50%;
        right: 0.75rem;
        transform: translateY(-50%);
    }

    &.--tooltip {
        .input-currency__wrapper {
            &::after {
                right: 2.25rem;
            }
        }

        input {
            padding-right: 3.5rem !important;
        }
    }
    &.--text-left {
        .input-currency__price {
            text-align: left;
        }
    }
}
</style>